import { ValidationError } from "@/types/auth";

export const validateEmail = (email: string): string | null => {
  if (!email) {
    return "Por favor, informe seu e-mail";
  }

  if (email.length > 500) {
    return "E-mail muito longo. Use no máximo 500 caracteres";
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return "Digite um e-mail válido (exemplo: seu@email.com)";
  }

  return null;
};

export const validatePassword = (password: string): string | null => {
  if (!password) {
    return "Por favor, informe sua senha";
  }

  if (password.length < 8) {
    return "Sua senha precisa ter pelo menos 8 caracteres";
  }

  if (password.length > 255) {
    return "Senha muito longa. Use no máximo 255 caracteres";
  }

  const hasNumber = /\d/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasUppercase = /[A-Z]/.test(password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

  if (!hasNumber || !hasLowercase || !hasUppercase || !hasSpecialChar) {
    return "Sua senha precisa ter: um número, uma letra minúscula, uma maiúscula e um caractere especial (!@#$%^&*)";
  }

  return null;
};

export const validateName = (name: string): string | null => {
  if (!name) {
    return "Por favor, informe seu nome completo";
  }

  if (name.length > 100) {
    return "Nome muito longo. Use no máximo 100 caracteres";
  }

  return null;
};

export const validateAccountName = (name: string): string | null => {
  if (!name) {
    return "Por favor, informe o nome da sua conta";
  }

  if (name.length > 100) {
    return "Nome da conta muito longo. Use no máximo 100 caracteres";
  }

  return null;
};

export const validateDomain = (domain: string): string | null => {
  if (!domain) {
    return null;
  }

  if (domain.length > 100) {
    return "Domínio muito longo. Use no máximo 100 caracteres";
  }

  // Remove espaços em branco
  const cleanDomain = domain.trim();

  // Verifica se contém protocolos (http, https)
  if (cleanDomain.includes("http://") || cleanDomain.includes("https://")) {
    return "Digite apenas o domínio, sem http:// ou https:// (exemplo: seudominio.com.br)";
  }

  // Verifica se contém www
  if (cleanDomain.startsWith("www.")) {
    return "Digite apenas o domínio, sem www (exemplo: seudominio.com.br)";
  }

  // Verifica se contém barras ou outros caracteres de URL
  if (cleanDomain.includes("/") || cleanDomain.includes("?")) {
    return "Digite apenas o domínio, sem caminhos ou parâmetros (exemplo: seudominio.com.br)";
  }

  // Verifica se contém espaços
  if (cleanDomain.includes(" ")) {
    return "O domínio não pode conter espaços";
  }

  // Verifica se o formato do domínio é válido
  const domainRegex =
    /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;
  if (!domainRegex.test(cleanDomain)) {
    return "Digite um domínio válido (exemplo: seudominio.com.br)";
  }

  return null;
};

export const formatCpfOrCnpj = (value: string): string => {
  const numbers = value.replace(/\D/g, "").slice(0, 14);

  // CPF: 000.000.000-00
  if (numbers.length <= 11) {
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6)
      return `${numbers.slice(0, 3)}.${numbers.slice(3)}`;
    if (numbers.length <= 9)
      return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6)}`;
    return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(
      6,
      9,
    )}-${numbers.slice(9, 11)}`;
  }

  // CNPJ: 00.000.000/0000-00
  if (numbers.length <= 2) return numbers;
  if (numbers.length <= 5) return `${numbers.slice(0, 2)}.${numbers.slice(2)}`;
  if (numbers.length <= 8)
    return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(5)}`;
  if (numbers.length <= 12)
    return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(
      5,
      8,
    )}/${numbers.slice(8)}`;
  return `${numbers.slice(0, 2)}.${numbers.slice(2, 5)}.${numbers.slice(
    5,
    8,
  )}/${numbers.slice(8, 12)}-${numbers.slice(12, 14)}`;
};

// Dígito verificador (módulo 11) a partir dos pesos informados.
const checkDigit = (digits: string, weights: number[]): number => {
  const sum = weights.reduce((total, weight, i) => total + Number(digits[i]) * weight, 0);
  const rest = sum % 11;
  return rest < 2 ? 0 : 11 - rest;
};

// Sequências repetidas (000.000.000-00, 11.111.111/1111-11...) passam no
// cálculo dos dígitos, mas não são documentos válidos.
const isRepeated = (digits: string) => /^(\d)\1+$/.test(digits);

export const isValidCpf = (value: string): boolean => {
  const digits = value.replace(/\D/g, "");
  if (digits.length !== 11 || isRepeated(digits)) return false;
  const first = checkDigit(digits, [10, 9, 8, 7, 6, 5, 4, 3, 2]);
  const second = checkDigit(digits, [11, 10, 9, 8, 7, 6, 5, 4, 3, 2]);
  return first === Number(digits[9]) && second === Number(digits[10]);
};

export const isValidCnpj = (value: string): boolean => {
  const digits = value.replace(/\D/g, "");
  if (digits.length !== 14 || isRepeated(digits)) return false;
  const first = checkDigit(digits, [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
  const second = checkDigit(digits, [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
  return first === Number(digits[12]) && second === Number(digits[13]);
};

export const validateCpfOrCnpj = (document: string): string | null => {
  if (!document || !document.trim()) {
    return "CPF ou CNPJ é obrigatório";
  }

  const numbers = document.replace(/\D/g, "");

  if (numbers.length === 11) {
    return isValidCpf(numbers) ? null : "CPF inválido. Confira os números digitados.";
  }

  if (numbers.length === 14) {
    return isValidCnpj(numbers) ? null : "CNPJ inválido. Confira os números digitados.";
  }

  return "Informe um CPF com 11 dígitos ou um CNPJ com 14 dígitos";
};

export const getCpfCnpjPayload = (
  document: string,
): { cpf?: string; cnpj?: string } => {
  const numbers = document.replace(/\D/g, "");

  if (numbers.length === 11) {
    return { cpf: numbers };
  }

  if (numbers.length === 14) {
    return { cnpj: numbers };
  }

  return {};
};

export const formatPhone = (value: string): string => {
  const numbers = value.replace(/\D/g, "").slice(0, 11);

  if (numbers.length <= 2) return numbers;
  if (numbers.length <= 7)
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
  return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(
    7,
    11,
  )}`;
};

/**
 * Telefone com código do país (formulários que enviam lead à RD Station).
 * Brasil (+55) ganha a máscara completa, `+55 (11) 99999-8888` ou
 * `+55 (11) 3333-4444`; outros países ficam `+` e os dígitos (até 15, E.164),
 * já que o tamanho do código varia.
 */
export const formatIntlPhone = (value: string): string => {
  const d = value.replace(/\D/g, "").slice(0, 15);
  if (!d) return "";
  if (!d.startsWith("55")) return `+${d}`;
  const local = d.slice(2, 13);
  const ddd = local.slice(0, 2);
  const rest = local.slice(2);
  if (!ddd) return "+55";
  if (!rest) return `+55 (${ddd}`;
  if (rest.length <= 4) return `+55 (${ddd}) ${rest}`;
  const split = rest.length === 9 ? 5 : 4;
  return `+55 (${ddd}) ${rest.slice(0, split)}-${rest.slice(split)}`;
};

/** Brasil: 55 + DDD + 8 ou 9 dígitos; outros países: de 10 a 15 dígitos no total. */
export const validateIntlPhone = (phone: string): string | null => {
  const d = phone.replace(/\D/g, "");
  const valid = d.startsWith("55") ? /^55\d{10,11}$/.test(d) : /^\d{10,15}$/.test(d);
  return valid ? null : "Telefone inválido. Use código do país, DDD e número.";
};

/** Formato pedido pela RD Station: + código do país + DDD + número (+5511999998888). */
export const toRdPhone = (phone: string): string => `+${phone.replace(/\D/g, "")}`;

/**
 * Formato da nossa API (como sempre foi: DDD + número, só dígitos). O +55 do
 * Brasil sai; de outros países não dá para saber o tamanho do código, então
 * vão todos os dígitos.
 */
export const toApiPhone = (phone: string): string => {
  const d = phone.replace(/\D/g, "");
  return d.startsWith("55") ? d.slice(2) : d;
};

export const validatePhone = (phone: string): string | null => {
  if (!phone || !phone.trim()) {
    return null;
  }

  const numbers = phone.replace(/\D/g, "");
  if (numbers.length < 10 || numbers.length > 11) {
    return "Telefone deve ter 10 ou 11 dígitos (com DDD)";
  }

  return null;
};

export const validateLoginForm = (
  email: string,
  password: string,
): ValidationError[] => {
  const errors: ValidationError[] = [];

  const emailError = validateEmail(email);
  if (emailError) {
    errors.push({ field: "email", message: emailError });
  }

  // Validação simples para senha no login (apenas se está preenchida)
  if (!password) {
    errors.push({ field: "password", message: "Por favor, informe sua senha" });
  }

  return errors;
};

export const validateRegisterForm = (data: {
  name: string;
  email: string;
  password: string;
  accountName: string;
  domain: string;
}): ValidationError[] => {
  const errors: ValidationError[] = [];

  const nameError = validateName(data.name);
  if (nameError) {
    errors.push({ field: "name", message: nameError });
  }

  const emailError = validateEmail(data.email);
  if (emailError) {
    errors.push({ field: "email", message: emailError });
  }

  const passwordError = validatePassword(data.password);
  if (passwordError) {
    errors.push({ field: "password", message: passwordError });
  }

  const accountNameError = validateAccountName(data.accountName);
  if (accountNameError) {
    errors.push({ field: "accountName", message: accountNameError });
  }

  const domainError = validateDomain(data.domain);
  if (domainError) {
    errors.push({ field: "domain", message: domainError });
  }

  return errors;
};

export const validateUrl = (url: string): boolean => {
  if (!url) {
    return false;
  }

  try {
    // Adicionar protocolo se não existir
    const urlToTest =
      url.startsWith("http://") || url.startsWith("https://")
        ? url
        : `https://${url}`;

    new URL(urlToTest);
    return true;
  } catch {
    return false;
  }
};

export const validateUrlWithMessage = (url: string): string | null => {
  if (!url.trim()) {
    return "URL é obrigatória";
  }

  if (url.length > 500) {
    return "URL muito longa. Use no máximo 500 caracteres";
  }

  // Verificar se começa com http:// ou https://
  if (!url.startsWith("http://") && !url.startsWith("https://")) {
    return "URL deve começar com http:// ou https:// (exemplo: https://exemplo.com)";
  }

  // Verificar se contém pelo menos um ponto (domínio básico)
  if (!url.includes(".")) {
    return "Digite uma URL válida (exemplo: https://exemplo.com)";
  }

  try {
    new URL(url);
    return null;
  } catch {
    return "Digite uma URL válida (exemplo: https://exemplo.com)";
  }
};

export const validateUrlInRealTime = (url: string): string | null => {
  if (!url.trim()) {
    return null; // Não mostrar erro para campo vazio em tempo real
  }

  // Verificar se começa com http:// ou https://
  if (!url.startsWith("http://") && !url.startsWith("https://")) {
    return "URL deve começar com http:// ou https://";
  }

  // Verificar se contém pelo menos um ponto (domínio básico)
  if (!url.includes(".")) {
    return "URL deve conter um domínio válido (exemplo: https://exemplo.com)";
  }

  // Verificar se não contém espaços
  if (url.includes(" ")) {
    return "URL não pode conter espaços";
  }

  // Verificar se não contém caracteres inválidos
  if (/[<>"{}|\\^`\[\]]/.test(url)) {
    return "URL contém caracteres inválidos";
  }

  // Verificar se tem pelo menos 12 caracteres (mínimo para https://a.b)
  if (url.length < 12) {
    return "URL muito curta";
  }

  return null;
};

// Link de rede social: opcional e só precisa ser uma URL válida (não
// conferimos se o domínio é da rede escolhida).
export const validateSocialMediaUrl = (url: string): string | null =>
  url.trim() ? validateUrlWithMessage(url) : null;

export const validateToken = (token: string): string | null => {
  if (!token) {
    return "Token é obrigatório";
  }

  if (token.length < 10) {
    return "Token inválido";
  }

  return null;
};

export const validateResetPasswordForm = (email: string): ValidationError[] => {
  const errors: ValidationError[] = [];

  const emailError = validateEmail(email);
  if (emailError) {
    errors.push({ field: "email", message: emailError });
  }

  return errors;
};

export const validateChangePasswordForm = (
  password: string,
): ValidationError[] => {
  const errors: ValidationError[] = [];

  const passwordError = validatePassword(password);
  if (passwordError) {
    errors.push({ field: "password", message: passwordError });
  }

  return errors;
};
